import { useState } from 'react';

import { AlertCircleIcon, SpinnerIcon } from '@/components/icons';
import { Button } from '@/components/ui/Button';
import { Textarea } from '@/components/ui/Textarea';
import { SectionHeading } from '@/components/portal/PortalPage';
import type { ApiError } from '@/lib/api';

import type { SatisfactionAnswers, SatisfactionContext } from './types';

const RATINGS = [1, 2, 3, 4, 5];

const RATING_LABELS: Record<number, string> = {
  1: 'Très insatisfait',
  2: 'Insatisfait',
  3: 'Neutre',
  4: 'Satisfait',
  5: 'Très satisfait',
};

/**
 * Questionnaire de fin de mois, à remplir avant d'envoyer son CRA.
 *
 * ── Pourquoi il est reçu du serveur et non codé ici ──────────────────────
 * Les questions sont administrables et leur composition dépend de l'acteur :
 * un consultant et un interne ne répondent pas aux mêmes. Les écrire dans ce
 * fichier obligerait à livrer le portail à chaque changement de formulaire, et
 * la `version` transmise ne voudrait plus rien dire.
 *
 * Le moment n'est pas un hasard non plus : c'est la seule occasion mensuelle
 * d'entendre un consultant en clientèle, et l'adosser à un geste qu'il fait de
 * toute façon évite le questionnaire qu'on n'ouvre jamais.
 */
export const SatisfactionDialog = ({
  context,
  loading,
  error,
  pending,
  onCancel,
  onSubmit,
  onRetry,
}: {
  context: SatisfactionContext | undefined;
  loading: boolean;
  error: ApiError | null;
  pending: boolean;
  onCancel: () => void;
  onSubmit: (answers: SatisfactionAnswers) => void;
  onRetry: () => void;
}) => {
  const [answers, setAnswers] = useState<SatisfactionAnswers>({});
  const [showErrors, setShowErrors] = useState(false);

  const missing = (context?.questions ?? []).filter(
    (question) =>
      question.required &&
      (answers[question.id] === undefined || String(answers[question.id]).trim() === ''),
  );

  return (
    <section aria-label="Questionnaire de fin de mois" className="portal-surface">
      {loading ? (
        <p role="status" className="portal-loading">
          <SpinnerIcon className="text-base" />
          Chargement du questionnaire…
        </p>
      ) : !context ? (
        <div role="alert" className="portal-notice">
          {error?.message ?? 'Le questionnaire est indisponible.'}
          <Button variant="secondary" size="sm" onClick={onRetry}>
            Réessayer
          </Button>
        </div>
      ) : (
        <>
          <SectionHeading
            title={context.title}
            description={context.description}
            action={
              <span className="portal-count">
                {
                  context.questions.filter(
                    (q) => answers[q.id] !== undefined && String(answers[q.id]).trim() !== '',
                  ).length
                }{' '}
                / {context.questions.length} réponses
              </span>
            }
          />

          <div>
            {context.questions.map((question) => {
              const invalid = showErrors && missing.includes(question);
              /* Nommé `fieldError` et non `error` : la prop du même nom porte
                 l'erreur d'envoi, et l'ombrer rendrait la lecture ambiguë. */
              const fieldError = invalid ? 'Cette réponse est attendue.' : undefined;

              /* Une question libre est un simple champ : son libellé est porté
                 par le champ lui-même. Un `fieldset` par-dessus répéterait le
                 texte pour les lecteurs d'écran. */
              if (question.type === 'text') {
                return (
                  <Textarea
                    key={question.id}
                    className="portal-question"
                    disabled={pending}
                    label={question.required ? `${question.label} *` : question.label}
                    rows={3}
                    error={fieldError}
                    value={String(answers[question.id] ?? '')}
                    onChange={(event) => {
                      setAnswers({ ...answers, [question.id]: event.target.value });
                    }}
                  />
                );
              }

              return (
                <fieldset
                  key={question.id}
                  className="portal-question"
                  disabled={pending}
                  aria-describedby={fieldError ? `error-${question.id}` : undefined}
                >
                  <legend className="text-navy-900 mb-2 text-sm font-medium">
                    {question.label}
                    {question.required ? <span className="text-ink-400"> *</span> : null}
                  </legend>

                  {/* Boutons et non liste déroulante : cinq valeurs se
                      comparent d'un regard, et le geste tient en un clic. */}
                  <div className="portal-ratings">
                    {RATINGS.map((rating) => {
                      const selected = answers[question.id] === rating;

                      return (
                        <button
                          key={rating}
                          type="button"
                          aria-pressed={selected}
                          aria-label={`${rating} sur 5 — ${RATING_LABELS[rating] ?? ''}`}
                          title={RATING_LABELS[rating]}
                          onClick={() => {
                            setAnswers({ ...answers, [question.id]: rating });
                          }}
                          className="portal-rating"
                        >
                          <strong>{rating}</strong>
                          <small>{RATING_LABELS[rating]}</small>
                        </button>
                      );
                    })}
                  </div>

                  {fieldError ? (
                    <p
                      id={`error-${question.id}`}
                      role="alert"
                      className="mt-1.5 text-sm text-red-700"
                    >
                      {fieldError}
                    </p>
                  ) : null}
                </fieldset>
              );
            })}
          </div>
        </>
      )}

      {error && context ? (
        <p
          role="alert"
          className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800"
        >
          <AlertCircleIcon className="mt-0.5 shrink-0 text-base" />
          {error.message}
        </p>
      ) : null}

      <div className="portal-form-footer">
        <Button type="button" variant="secondary" disabled={pending} onClick={onCancel}>
          Revenir au CRA
        </Button>
        <Button
          type="button"
          loading={pending}
          loadingLabel="Envoi…"
          disabled={!context}
          onClick={() => {
            setShowErrors(true);
            if (missing.length > 0) return;

            onSubmit(answers);
          }}
        >
          Envoyer mon CRA
        </Button>
      </div>
    </section>
  );
};
